import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth, Organization, ConsultingFirm, app } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import LanguageSelector from '../components/LanguageSelector';
import { CloneOrgModal, StatCard, OrgCard, OrgManageUsers } from '../components/shared';
import { Timestamp } from 'firebase/firestore';
import './FirmDashboard.css';

interface Consultant {
  id: string;
  email: string;
  name: string;
  assignedClientOrgIds?: string[];
}

interface FirmDashboardData {
  firm: ConsultingFirm;
  consultants: Consultant[];
  clientOrgs: Organization[];
  stats: {
    consultantCount: number;
    clientCount: number;
  };
}

interface FirmListItem {
  id: string;
  name: string;
  leadEmail: string;
  isActive: boolean;
  consultantCount?: number;
  clientCount?: number;
  branding?: {
    firmName?: string;
    logo?: string;
  };
}

// Helper function to format Timestamp for display
const formatDate = (timestamp: Timestamp | { _seconds: number; _nanoseconds: number } | undefined): string => {
  if (!timestamp) return '-';

  let date: Date;
  if (typeof (timestamp as Timestamp).toDate === 'function') {
    // It's a Firestore Timestamp
    date = (timestamp as Timestamp).toDate();
  } else if ('_seconds' in timestamp) {
    // It's a serialized timestamp from the API
    date = new Date(timestamp._seconds * 1000);
  } else {
    return '-';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const FirmDashboard: React.FC = () => {
  const {
    user,
    firm,
    isFirmLead,
    isConsultant,
    isAdmin,
    selectOrganization,
    cloneOrganization,
    deleteOrganization,
    logout,
  } = useAuth();
  const { t, language } = useLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dashboardData, setDashboardData] = useState<FirmDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Admin-specific state for viewing all firms
  const [allFirms, setAllFirms] = useState<FirmListItem[]>([]);
  const [selectedFirmId, setSelectedFirmId] = useState<string | null>(null);
  const [isLoadingFirms, setIsLoadingFirms] = useState(false);

  // New client org form
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientLanguage, setNewClientLanguage] = useState<'he' | 'en' | 'ar'>(language);
  const [newClientEmoji, setNewClientEmoji] = useState('');
  const [newClientVfp, setNewClientVfp] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  // New consultant form (firm lead only)
  const [showNewConsultantForm, setShowNewConsultantForm] = useState(false);
  const [newConsultantEmail, setNewConsultantEmail] = useState('');
  const [newConsultantName, setNewConsultantName] = useState('');
  const [isAddingConsultant, setIsAddingConsultant] = useState(false);

  // Assignment modal
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);

  // Edit consultant modal
  const [showEditConsultantModal, setShowEditConsultantModal] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState<Consultant | null>(null);
  const [editConsultantName, setEditConsultantName] = useState('');
  const [isSavingConsultant, setIsSavingConsultant] = useState(false);

  // Edit firm lead
  const [showChangeFirmLeadModal, setShowChangeFirmLeadModal] = useState(false);
  const [newFirmLeadEmail, setNewFirmLeadEmail] = useState('');
  const [isChangingLead, setIsChangingLead] = useState(false);

  // Org users modal
  const [showOrgUsersModal, setShowOrgUsersModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  // Clone organization modal
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSourceOrg, setCloneSourceOrg] = useState<Organization | null>(null);

  const functions = getFunctions(app, 'us-central1');

  // Load all firms for admin users
  const loadAllFirms = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingFirms(true);
    try {
      const listConsultingFirms = httpsCallable(functions, 'listConsultingFirms');
      const result = await listConsultingFirms({});
      const firms = (result.data as any).firms || [];
      setAllFirms(firms);

      // Check for firmId in URL query params
      const urlFirmId = searchParams.get('firmId');
      if (urlFirmId && firms.some((f: FirmListItem) => f.id === urlFirmId)) {
        setSelectedFirmId(urlFirmId);
        // Clear the query param after using it
        searchParams.delete('firmId');
        setSearchParams(searchParams, { replace: true });
      } else if (firms.length > 0 && !selectedFirmId) {
        // Auto-select first firm if none selected
        setSelectedFirmId(firms[0].id);
      }
    } catch (err: any) {
      console.error('Error loading firms:', err);
    }
    setIsLoadingFirms(false);
  }, [isAdmin, functions, selectedFirmId, searchParams, setSearchParams]);

  // Determine which firm ID to use for loading dashboard
  const activeFirmId = isAdmin ? selectedFirmId : firm?.id;

  const loadDashboard = useCallback(async () => {
    if (!activeFirmId) return;

    setIsLoading(true);
    setError(null);

    try {
      const getFirmDashboard = httpsCallable(functions, 'getFirmDashboard');
      const result = await getFirmDashboard({ firmId: activeFirmId });
      setDashboardData(result.data as FirmDashboardData);
    } catch (err: any) {
      console.error('Error loading firm dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    }

    setIsLoading(false);
  }, [activeFirmId, functions]);

  // Load all firms for admin on mount
  useEffect(() => {
    if (isAdmin) {
      loadAllFirms();
    }
  }, [isAdmin, loadAllFirms]);

  // Load dashboard when firm ID changes
  useEffect(() => {
    if (activeFirmId) {
      loadDashboard();
    }
  }, [activeFirmId, loadDashboard]);

  const handleCreateClientOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClientName.trim() || !activeFirmId) return;

    setIsCreatingClient(true);
    try {
      const createClientOrganization = httpsCallable(functions, 'createClientOrganization');
      await createClientOrganization({
        firmId: activeFirmId,
        name: newClientName.trim(),
        language: newClientLanguage,
        emoji: newClientEmoji || undefined,
        companyVfp: newClientVfp || undefined,
      });

      setNewClientName('');
      setNewClientEmoji('');
      setNewClientVfp('');
      setShowNewClientForm(false);
      await loadDashboard();
    } catch (err: any) {
      console.error('Error creating client org:', err);
      alert(err.message || 'Failed to create client organization');
    }
    setIsCreatingClient(false);
  };

  const handleAddConsultant = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleAddConsultant called', { newConsultantEmail, activeFirmId });
    if (!newConsultantEmail.trim() || !activeFirmId) {
      console.log('Early return - missing email or firmId');
      return;
    }

    setIsAddingConsultant(true);
    try {
      console.log('Calling addConsultantToFirm with:', {
        firmId: activeFirmId,
        email: newConsultantEmail.trim(),
        name: newConsultantName.trim() || newConsultantEmail.split('@')[0],
      });
      const addConsultantToFirm = httpsCallable(functions, 'addConsultantToFirm');
      const result = await addConsultantToFirm({
        firmId: activeFirmId,
        email: newConsultantEmail.trim(),
        name: newConsultantName.trim() || newConsultantEmail.split('@')[0],
      });
      console.log('addConsultantToFirm result:', result);

      setNewConsultantEmail('');
      setNewConsultantName('');
      setShowNewConsultantForm(false);
      await loadDashboard();
    } catch (err: any) {
      console.error('Error adding consultant:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      alert(err.message || 'Failed to add consultant');
    }
    setIsAddingConsultant(false);
  };

  const handleRemoveConsultant = async (consultantId: string) => {
    if (!activeFirmId) return;
    if (!window.confirm('Are you sure you want to remove this consultant?')) return;

    try {
      const removeConsultantFromFirm = httpsCallable(functions, 'removeConsultantFromFirm');
      await removeConsultantFromFirm({
        firmId: activeFirmId,
        userId: consultantId,
      });
      await loadDashboard();
    } catch (err: any) {
      console.error('Error removing consultant:', err);
      alert(err.message || 'Failed to remove consultant');
    }
  };

  const handleAssignClient = async (consultantId: string, orgId: string) => {
    if (!activeFirmId) return;

    try {
      const assignClientToConsultant = httpsCallable(functions, 'assignClientToConsultant');
      await assignClientToConsultant({
        firmId: activeFirmId,
        consultantId,
        organizationId: orgId,
      });
      await loadDashboard();
    } catch (err: any) {
      console.error('Error assigning client:', err);
      alert(err.message || 'Failed to assign client');
    }
  };

  const handleUnassignClient = async (consultantId: string, orgId: string) => {
    if (!activeFirmId) return;

    try {
      const unassignClientFromConsultant = httpsCallable(functions, 'unassignClientFromConsultant');
      await unassignClientFromConsultant({
        firmId: activeFirmId,
        consultantId,
        organizationId: orgId,
      });
      await loadDashboard();
    } catch (err: any) {
      console.error('Error unassigning client:', err);
      alert(err.message || 'Failed to unassign client');
    }
  };

  const handleOpenOrgBoard = async (org: Organization) => {
    await selectOrganization(org.id);
    navigate('/');
  };

  const handleOpenOrgSettings = async (org: Organization) => {
    await selectOrganization(org.id);
    navigate('/?openSettings=true');
  };

  const handleFirmChange = (firmId: string) => {
    setSelectedFirmId(firmId);
    setDashboardData(null);
  };

  const handleEditConsultant = (consultant: Consultant) => {
    setEditingConsultant(consultant);
    setEditConsultantName(consultant.name);
    setShowEditConsultantModal(true);
  };

  const handleSaveConsultant = async () => {
    if (!editingConsultant || !editConsultantName.trim()) return;

    setIsSavingConsultant(true);
    try {
      const updateConsultant = httpsCallable(functions, 'updateConsultant');
      await updateConsultant({
        firmId: activeFirmId,
        userId: editingConsultant.id,
        name: editConsultantName.trim(),
      });
      setShowEditConsultantModal(false);
      setEditingConsultant(null);
      await loadDashboard();
    } catch (err: any) {
      console.error('Error updating consultant:', err);
      alert(err.message || 'Failed to update consultant');
    }
    setIsSavingConsultant(false);
  };

  const handleChangeFirmLead = async () => {
    if (!newFirmLeadEmail.trim() || !activeFirmId) return;

    setIsChangingLead(true);
    try {
      const updateConsultingFirm = httpsCallable(functions, 'updateConsultingFirm');
      await updateConsultingFirm({
        firmId: activeFirmId,
        leadEmail: newFirmLeadEmail.trim(),
      });
      setShowChangeFirmLeadModal(false);
      setNewFirmLeadEmail('');
      await loadDashboard();
      // Reload firms list to update lead info
      if (isAdmin) {
        await loadAllFirms();
      }
    } catch (err: any) {
      console.error('Error changing firm lead:', err);
      alert(err.message || 'Failed to change firm lead');
    }
    setIsChangingLead(false);
  };

  const handleOpenOrgUsers = (org: Organization) => {
    setSelectedOrg(org);
    setShowOrgUsersModal(true);
  };

  const handleOpenCloneModal = (org: Organization) => {
    setCloneSourceOrg(org);
    setShowCloneModal(true);
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setCloneSourceOrg(null);
  };

  const handleDeleteOrg = async (org: Organization) => {
    if (!window.confirm(`Are you sure you want to delete "${org.name}"? This will permanently delete all org data and cannot be undone.`)) {
      return;
    }

    try {
      await deleteOrganization(org.id);
      await loadDashboard();
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      alert(err.message || 'Failed to delete organization');
    }
  };

  const handleCloneOrg = async (newName: string, includeEmployees: boolean) => {
    if (!cloneSourceOrg || !activeFirmId) return;

    try {
      await cloneOrganization(cloneSourceOrg.id, newName, includeEmployees, activeFirmId);
      handleCloseCloneModal();
      await loadDashboard();
    } catch (err: any) {
      console.error('Error cloning organization:', err);
      alert(err.message || 'Failed to clone organization');
      throw err; // Re-throw so modal knows it failed
    }
  };

  // For non-admin users without a firm
  if (!firm && !isAdmin) {
    return (
      <div className="firm-dashboard">
        <div className="firm-header">
          <h1>{t('firm.dashboard')}</h1>
          <div className="header-actions">
            <LanguageSelector />
            <Link to="/" className="back-link">{t('firm.backToBoard')}</Link>
            <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
          </div>
        </div>
        <div className="firm-content">
          <div className="error-message">
            {t('firm.notAssociated')}
          </div>
        </div>
      </div>
    );
  }

  // Admin view when no firms exist
  if (isAdmin && allFirms.length === 0 && !isLoadingFirms) {
    return (
      <div className="firm-dashboard">
        <div className="firm-header">
          <h1>{t('firm.dashboard')}</h1>
          <div className="header-actions">
            <LanguageSelector />
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{t('firm.platformAdmin')}</span>
            </div>
            <Link to="/" className="back-link">{t('firm.backToBoard')}</Link>
            <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
          </div>
        </div>
        <div className="firm-content">
          <div className="empty-state">
            {t('firm.noFirms')} <Link to="/admin">{t('firm.createInAdmin')}</Link>.
          </div>
        </div>
      </div>
    );
  }

  // Get display name for loading/error states
  const getDisplayName = () => {
    if (dashboardData?.firm) return dashboardData.firm.branding?.firmName || dashboardData.firm.name;
    if (isAdmin && selectedFirmId) {
      const selectedFirm = allFirms.find(f => f.id === selectedFirmId);
      return selectedFirm?.branding?.firmName || selectedFirm?.name || t('firm.dashboard');
    }
    return firm?.branding?.firmName || firm?.name || t('firm.dashboard');
  };

  if (isLoading || isLoadingFirms) {
    return (
      <div className="firm-dashboard">
        <div className="firm-header">
          <h1>{getDisplayName()}</h1>
          <div className="header-actions">
            <LanguageSelector />
            <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
          </div>
        </div>
        <div className="firm-content">
          <div className="loading">{t('firm.loadingDashboard')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="firm-dashboard">
        <div className="firm-header">
          <h1>{getDisplayName()}</h1>
          <div className="header-actions">
            <LanguageSelector />
            <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
          </div>
        </div>
        <div className="firm-content">
          <div className="error-message">{error}</div>
          <button onClick={loadDashboard} className="btn-primary">{t('general.retry')}</button>
        </div>
      </div>
    );
  }

  // Get display firm from dashboardData or selected firm
  const displayFirm = dashboardData?.firm || (isAdmin ? allFirms.find(f => f.id === selectedFirmId) : firm);
  const canManageFirm = isAdmin || isFirmLead;

  return (
    <div className="firm-dashboard">
      <div className="firm-header">
        {displayFirm?.branding?.logo && (
          <img src={displayFirm.branding.logo} alt={displayFirm.name} className="firm-logo" />
        )}

        {/* Admin firm selector */}
        {isAdmin && allFirms.length > 0 ? (
          <div className="firm-selector">
            <label>{t('firm.viewingFirm')}</label>
            <select
              value={selectedFirmId || ''}
              onChange={(e) => handleFirmChange(e.target.value)}
              className="firm-select"
            >
              {allFirms.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.clientCount || 0} {t('admin.clients')}, {f.consultantCount || 0} {t('firm.consultants').toLowerCase()})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <h1>{displayFirm?.branding?.firmName || displayFirm?.name || t('firm.dashboard')}</h1>
        )}

        <div className="header-actions">
          <LanguageSelector />
          <div className="user-info">
            <span className="user-name">{user?.name}</span>
            <span className="user-role">{isAdmin ? t('firm.platformAdmin') : user?.role?.replace('_', ' ')}</span>
          </div>
          <Link to="/" className="back-link">{t('firm.backToBoard')}</Link>
          <button onClick={logout} className="btn-logout">{t('nav.logout')}</button>
        </div>
      </div>

      <div className="firm-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <StatCard
            value={dashboardData?.stats.clientCount || 0}
            label={t('firm.clientOrganizations')}
          />
          {canManageFirm && (
            <StatCard
              value={dashboardData?.stats.consultantCount || 0}
              label={t('firm.consultants')}
            />
          )}
        </div>

        {/* Client Organizations Section */}
        <div className="firm-section">
          <div className="section-header">
            <h2>📊 {t('firm.clientOrganizations')}</h2>
            <button
              className="btn-add"
              onClick={() => setShowNewClientForm(true)}
            >
              {t('firm.newClient')}
            </button>
          </div>

          {showNewClientForm && (
            <form className="new-org-form-card" onSubmit={handleCreateClientOrg}>
              <div className="form-row-inline">
                <div className="form-field">
                  <label>{t('firm.orgName')}</label>
                  <input
                    type="text"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder={t('firm.orgName')}
                    autoFocus
                    required
                  />
                </div>
                <div className="form-field form-field-small">
                  <label>{t('firm.language')}</label>
                  <select
                    value={newClientLanguage}
                    onChange={(e) => setNewClientLanguage(e.target.value as 'he' | 'en' | 'ar')}
                  >
                    <option value="he">🇮🇱 עברית</option>
                    <option value="en">🇺🇸 English</option>
                    <option value="ar">🇸🇦 العربية</option>
                  </select>
                </div>
                <div className="form-field form-field-small">
                  <label>{language === 'en' ? 'Emoji' : language === 'ar' ? 'رمز' : 'אימוג\'י'}</label>
                  <input
                    type="text"
                    value={newClientEmoji}
                    onChange={(e) => setNewClientEmoji(e.target.value)}
                    placeholder="🏢"
                    maxLength={2}
                    className="emoji-input"
                  />
                </div>
              </div>
              <div className="form-row-inline">
                <div className="form-field form-field-full">
                  <label>{language === 'en' ? 'Company VFP (Valuable Final Product)' : language === 'ar' ? 'المنتج النهائي القيّم' : 'מוצר סופי בעל ערך'}</label>
                  <textarea
                    value={newClientVfp}
                    onChange={(e) => setNewClientVfp(e.target.value)}
                    placeholder={language === 'en' ? 'High-quality products delivered to satisfied customers...' : language === 'ar' ? 'منتجات عالية الجودة تم تسليمها للعملاء الراضين...' : 'מוצרים ושירותים איכותיים שסופקו ללקוחות מרוצים...'}
                    rows={2}
                  />
                </div>
              </div>
              <div className="form-buttons">
                <button type="submit" className="btn-primary" disabled={isCreatingClient}>
                  {isCreatingClient ? t('admin.creating') : t('firm.create')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowNewClientForm(false);
                    setNewClientName('');
                    setNewClientEmoji('');
                    setNewClientVfp('');
                  }}
                >
                  {t('general.cancel')}
                </button>
              </div>
            </form>
          )}

          <div className="client-list">
            {dashboardData?.clientOrgs.map((org) => (
              <OrgCard
                key={org.id}
                org={{ ...org, emoji: org.emoji || '🏢' }}
                showLanguage={true}
                actions={[
                  { label: t('firm.openBoard'), onClick: () => handleOpenOrgBoard(org), variant: 'primary' },
                  { label: t('firm.settings'), onClick: () => handleOpenOrgSettings(org), variant: 'secondary' },
                  { label: t('firm.users'), onClick: () => handleOpenOrgUsers(org), variant: 'secondary' },
                  { label: t('firm.clone'), onClick: () => handleOpenCloneModal(org), variant: 'clone' },
                  { label: t('general.delete'), onClick: () => handleDeleteOrg(org), variant: 'danger' },
                ]}
              >
                <div className="org-dates">
                  <span className="org-date">{t('firm.created')}: {formatDate(org.createdAt)}</span>
                  {org.updatedAt && <span className="org-date">{t('firm.updated')}: {formatDate(org.updatedAt)}</span>}
                </div>
              </OrgCard>
            ))}
            {(!dashboardData?.clientOrgs || dashboardData.clientOrgs.length === 0) && (
              <div className="empty-state">
                {t('firm.noClients')}
              </div>
            )}
          </div>
        </div>

        {/* Firm Lead Section (Admin only) */}
        {isAdmin && dashboardData?.firm && (
          <div className="firm-section">
            <div className="section-header">
              <h2>👤 {t('firm.firmLead')}</h2>
              <button
                className="btn-add"
                onClick={() => setShowChangeFirmLeadModal(true)}
              >
                {t('firm.changeLead')}
              </button>
            </div>
            <div className="firm-lead-card">
              <div className="firm-lead-info">
                <h4>{dashboardData.firm.leadEmail || t('edit.notAssigned')}</h4>
                <span className="firm-lead-role">{t('firm.firmAdmin')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Consultants Section (Firm Lead and Admin) */}
        {canManageFirm && (
          <div className="firm-section">
            <div className="section-header">
              <h2>👥 {t('firm.consultants')}</h2>
              <button
                className="btn-add"
                onClick={() => setShowNewConsultantForm(true)}
              >
                {t('firm.addConsultant')}
              </button>
            </div>

            {showNewConsultantForm && (
              <form className="inline-form" onSubmit={handleAddConsultant}>
                <input
                  type="email"
                  placeholder={t('firm.emailAddress')}
                  value={newConsultantEmail}
                  onChange={(e) => setNewConsultantEmail(e.target.value)}
                  required
                  autoFocus
                />
                <input
                  type="text"
                  placeholder={t('firm.nameOptional')}
                  value={newConsultantName}
                  onChange={(e) => setNewConsultantName(e.target.value)}
                />
                <button type="submit" className="btn-primary" disabled={isAddingConsultant}>
                  {isAddingConsultant ? t('general.loading') : t('general.add')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowNewConsultantForm(false)}
                >
                  {t('general.cancel')}
                </button>
              </form>
            )}

            <div className="consultant-list">
              {dashboardData?.consultants.map((consultant) => (
                <div key={consultant.id} className="consultant-card">
                  <div className="consultant-info">
                    <h4>{consultant.name} {(consultant as any).isPending && <span className="pending-badge">({t('firm.pending')})</span>}</h4>
                    <span className="consultant-email">{consultant.email}</span>
                    <div className="consultant-assignments">
                      {consultant.assignedClientOrgIds?.length || 0} {t('firm.assignedClients')}
                    </div>
                  </div>
                  <div className="consultant-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => handleEditConsultant(consultant)}
                    >
                      {t('general.edit')}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setSelectedConsultant(consultant);
                        setShowAssignmentModal(true);
                      }}
                    >
                      {t('firm.assignments')}
                    </button>
                    <button
                      className="btn-danger"
                      onClick={() => handleRemoveConsultant(consultant.id)}
                    >
                      {t('general.remove')}
                    </button>
                  </div>
                </div>
              ))}
              {(!dashboardData?.consultants || dashboardData.consultants.length === 0) && (
                <div className="empty-state">
                  {t('firm.noConsultants')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {showAssignmentModal && selectedConsultant && (
        <div className="modal-overlay" onClick={() => setShowAssignmentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('firm.assignClientsTo', { name: selectedConsultant.name })}</h2>
              <button className="modal-close" onClick={() => setShowAssignmentModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="assignment-list">
                {dashboardData?.clientOrgs.map((org) => {
                  const isAssigned = selectedConsultant.assignedClientOrgIds?.includes(org.id);
                  return (
                    <div key={org.id} className="assignment-item">
                      <span className="org-name">
                        {org.emoji || '🏢'} {org.name}
                      </span>
                      {isAssigned ? (
                        <button
                          className="btn-danger-small"
                          onClick={() => handleUnassignClient(selectedConsultant.id, org.id)}
                        >
                          {t('general.remove')}
                        </button>
                      ) : (
                        <button
                          className="btn-primary-small"
                          onClick={() => handleAssignClient(selectedConsultant.id, org.id)}
                        >
                          {t('general.add')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAssignmentModal(false)}>
                {t('general.done')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Consultant Modal */}
      {showEditConsultantModal && editingConsultant && (
        <div className="modal-overlay" onClick={() => setShowEditConsultantModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('firm.editConsultant')}</h2>
              <button className="modal-close" onClick={() => setShowEditConsultantModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>{t('edit.name')}</label>
                <input
                  type="text"
                  value={editConsultantName}
                  onChange={(e) => setEditConsultantName(e.target.value)}
                  placeholder={t('firm.consultantName')}
                />
              </div>
              <div className="form-row">
                <label>{t('employees.email')}</label>
                <input
                  type="text"
                  value={editingConsultant.email}
                  disabled
                  className="disabled-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEditConsultantModal(false)}>
                {t('general.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveConsultant}
                disabled={isSavingConsultant}
              >
                {isSavingConsultant ? t('firm.saving') : t('general.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Firm Lead Modal */}
      {showChangeFirmLeadModal && (
        <div className="modal-overlay" onClick={() => setShowChangeFirmLeadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('firm.changeFirmLead')}</h2>
              <button className="modal-close" onClick={() => setShowChangeFirmLeadModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>{t('firm.newLeadEmail')}</label>
                <input
                  type="email"
                  value={newFirmLeadEmail}
                  onChange={(e) => setNewFirmLeadEmail(e.target.value)}
                  placeholder={t('firm.emailAddress')}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowChangeFirmLeadModal(false)}>
                {t('general.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleChangeFirmLead}
                disabled={isChangingLead || !newFirmLeadEmail.trim()}
              >
                {isChangingLead ? t('firm.changing') : t('firm.changeLead')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Org Users Modal */}
      {selectedOrg && (
        <OrgManageUsers
          organizationId={selectedOrg.id}
          organizationName={selectedOrg.name}
          isOpen={showOrgUsersModal}
          onClose={() => setShowOrgUsersModal(false)}
        />
      )}

      {/* Clone Organization Modal */}
      {showCloneModal && cloneSourceOrg && (
        <CloneOrgModal
          org={cloneSourceOrg}
          onClone={handleCloneOrg}
          onClose={handleCloseCloneModal}
        />
      )}

    </div>
  );
};

export default FirmDashboard;
